/**
 * Runtime environment configuration.
 *
 * The console is compiled **once** and the same standalone bundle is served by
 * every PR preview. `NEXT_PUBLIC_*` values are inlined at build time, so they
 * cannot carry per-PR API hosts/ports. Instead each preview injects its config
 * at runtime via `/runtime-env.js`, which sets `window.RUNTIME_ENV` (Docker
 * entrypoint) or `window.__ENV` (static default/tests) before the app boots
 * (loaded by a `beforeInteractive` <Script> in `layout.tsx`).
 *
 * Readers below prefer the browser runtime env and fall back to process env for
 * SSR/build/non-preview contexts so existing dev/staging/prod behavior is
 * unchanged when no runtime env is present.
 */

export type RuntimeEnv = {
  /** Per-PR API origin, e.g. https://pr-1234-api.preview.decodahealth.com */
  API_BASE_URL?: string;
  NEXT_PUBLIC_API_BASE_URL?: string;
  /** Browser-facing Firebase Auth emulator host, e.g. pr-1234-auth.preview.decodahealth.com */
  FIREBASE_AUTH_EMULATOR_HOST?: string;
  NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST?: string;
  NEXT_PUBLIC_POSTHOG_KEY?: string;
  NEXT_PUBLIC_POSTHOG_HOST?: string;
  NEXT_PUBLIC_SERVICE_BANNER_ENABLED?: string;
  NEXT_PUBLIC_SERVICE_BANNER_MESSAGE?: string;
  NEXT_PUBLIC_SERVICE_BANNER_SEVERITY?: string;
  NEXT_PUBLIC_PYLON_APP_ID?: string;
  /** True when this process is serving a PR preview. */
  PREVIEW?: boolean | string;
  NEXT_PUBLIC_PREVIEW?: boolean | string;
};

declare global {
  interface Window {
    __ENV?: RuntimeEnv;
    RUNTIME_ENV?: RuntimeEnv;
  }
}

function parseBooleanFlag(
  value: boolean | string | undefined,
): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function windowEnv(): RuntimeEnv {
  if (typeof window === "undefined") return {};
  return { ...(window.RUNTIME_ENV ?? {}), ...(window.__ENV ?? {}) };
}

/** Per-PR API origin, or undefined when not configured. */
export function getRuntimeAPIBaseURL(): string | undefined {
  const env = windowEnv();
  return (
    env.API_BASE_URL ??
    env.NEXT_PUBLIC_API_BASE_URL ??
    process.env.API_BASE_URL ??
    process.env.NEXT_PUBLIC_API_BASE_URL
  );
}

/** Browser-facing Firebase Auth emulator host, or undefined when not configured. */
export function getRuntimeFirebaseAuthEmulatorHost(): string | undefined {
  const env = windowEnv();
  return (
    env.FIREBASE_AUTH_EMULATOR_HOST ??
    env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST ??
    process.env.FIREBASE_AUTH_EMULATOR_HOST ??
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST
  );
}

/** True when running inside a PR preview (runtime flag or build-time fallback). */
export function isPreview(): boolean {
  const env = windowEnv();
  const runtimePreview = parseBooleanFlag(
    env.PREVIEW ?? env.NEXT_PUBLIC_PREVIEW,
  );
  if (runtimePreview !== undefined) return runtimePreview;

  const processPreview = parseBooleanFlag(
    process.env.PREVIEW ?? process.env.NEXT_PUBLIC_PREVIEW,
  );
  if (processPreview !== undefined) return processPreview;

  const apiBaseURL = getRuntimeAPIBaseURL();
  if (apiBaseURL?.includes(".preview.decodahealth.com")) return true;

  if (typeof window !== "undefined") {
    return window.location.hostname.endsWith(".preview.decodahealth.com");
  }
  return false;
}
